import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { Eye, EyeOff, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1665476053468-a88d45087bdd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxoaWdoLXJpc2UlMjB0b3dlciUyMHVuZGVyJTIwY29uc3RydWN0aW9uJTIwc3Vuc2V0JTIwYWVyaWFsfGVufDB8fHx8MTc4NDAyMDg3MHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const quickFill = (e, p) => { setEmail(e); setPassword(p); };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-[1.15fr_1fr] bg-[#F8FAFC]">
      {/* Hero */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Construction" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0D3B66]/85 via-[#0D3B66]/50 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 xl:p-14 text-white">
          <div className="flex items-center gap-3">
            <img
              src="/brand/radiance-mark.png"
              alt="Radiance EPC"
              className="h-14 w-14 object-contain drop-shadow-lg"
              data-testid="login-brand-mark"
            />
            <div className="leading-tight">
              <div className="font-bold text-lg tracking-tight">RADIANCE</div>
              <div className="text-[11px] tracking-[0.3em] opacity-80">EPC</div>
            </div>
          </div>
          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-widest uppercase text-[#FBBF24] mb-4">
              <span className="h-px w-8 bg-[#FBBF24]" /> Engineering Procurement
            </div>
            <h1 className="text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight">
              Budget with clarity.<br/>
              <span className="text-[#FBBF24]">Approve with speed.</span>
            </h1>
            <p className="mt-5 text-white/80 text-base leading-relaxed">
              A single workflow from BOQ to indent, built for site engineers and approvers who move fast between the office and the site.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img
              src="/brand/radiance-mark.png"
              alt="Radiance EPC"
              className="h-11 w-11 object-contain"
              data-testid="login-mobile-brand-mark"
            />
            <div className="leading-tight">
              <div className="font-bold text-[#0D3B66] tracking-tight">RADIANCE</div>
              <div className="text-[10px] tracking-[0.3em] text-[#0D3B66]/70">EPC</div>
            </div>
          </div>

          <div className="text-sm font-semibold tracking-widest uppercase text-[#F97316] mb-3">Sign in</div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0D3B66] tracking-tight">Welcome back</h2>
          <p className="mt-2 text-slate-500">Enter your credentials to access your dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                data-testid="login-email-input"
                id="email" type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@radiance.com"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  data-testid="login-password-input"
                  id="password" type={showPw ? "text" : "password"} required autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  data-testid="login-toggle-password"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <Button
              type="submit" disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 rounded-full bg-[#0D3B66] hover:bg-[#114B82] text-white font-semibold group"
            >
              {loading ? "Signing in…" : (<span className="inline-flex items-center gap-2">Sign in <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>)}
            </Button>
          </form>

          <div className="mt-8 rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-[11px] font-semibold tracking-widest uppercase text-slate-500 mb-2">Demo accounts</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs text-slate-600">
              {[
                ["admin@radiance.com", "Admin@123", "Admin"],
                ["chairman@radiance.com", "Password@123", "Chairman"],
                ["eh@radiance.com", "Password@123", "Eng. Head"],
                ["bc@radiance.com", "Password@123", "Budget Ctrl"],
                ["engineer@radiance.com", "Password@123", "Site Engineer"],
              ].map(([e, p, label]) => (
                <button
                  type="button"
                  key={e}
                  data-testid={`demo-fill-${label.toLowerCase().replace(/\W+/g, "-")}`}
                  onClick={() => quickFill(e, p)}
                  className="flex items-center justify-between rounded-md border border-slate-100 px-2.5 py-2 hover:border-[#F97316] hover:bg-orange-50/40 text-left transition-colors"
                >
                  <span className="truncate">{label}</span>
                  <span className="text-slate-400 text-[10px] truncate">{e}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
